import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCandidates,
  getDashboard,
  getInterviews,
  getJobOpenings,
  getOffers,
} from "../api/recruitmentApi";
import "./HRDashboard.css";

const COMPANY_OPTIONS = [
  "Sheth Design Consultants LLP",
  "Softvolute Buildcon LLP",
  "Magnira Universe LLP",
  "SDC Studio LLC",
];

const LOCATION_OPTIONS = ["Surat", "Ahmedabad", "Houston (USA)", "Gandhinagar"];

const normalizeText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const isOfferLetterSent = (offer) =>
  offer?.offerLetterSent === true ||
  normalizeText(offer?.status) === "offer sent";

const sameValue = (first, second) =>
  normalizeText(first) === normalizeText(second);

const getOpeningCompany = (item) =>
  item?.company || item?.jobOpening?.company || "";
const getCandidateCompany = (item) =>
  item?.company || item?.jobOpening?.company || item?.jobOpeningCompany || "";
const getInterviewCompany = (item) =>
  item?.company ||
  item?.jobOpening?.company ||
  item?.candidate?.jobOpening?.company ||
  item?.candidate?.company ||
  "";
const getOfferCompany = (item) =>
  item?.company ||
  item?.jobOpening?.company ||
  item?.candidate?.jobOpening?.company ||
  item?.candidate?.company ||
  "";

const getOpeningLocation = (item) =>
  item?.location || item?.jobOpening?.location || "";
const getCandidateLocation = (item) =>
  item?.location ||
  item?.jobOpening?.location ||
  item?.jobOpeningLocation ||
  "";
const getInterviewLocation = (item) =>
  item?.location ||
  item?.jobOpening?.location ||
  item?.candidate?.jobOpening?.location ||
  item?.candidate?.location ||
  "";
const getOfferLocation = (item) =>
  item?.location ||
  item?.jobOpening?.location ||
  item?.candidate?.jobOpening?.location ||
  item?.candidate?.location ||
  "";

const Icon = ({ type, size = 28 }) => {
  const icons = {
    briefcase: "💼",
    candidates: "👥",
    interview: "🗓️",
    hired: "🧑‍💼",
    offer: "✉️",
    joined: "🤝",
    report: "📊",
    addCandidate: "👤",
    calendar: "📅",
    settings: "⚙️",
  };

  return (
    <span className="hrd-emoji-icon" style={{ fontSize: size }}>
      {icons[type] || "•"}
    </span>
  );
};

const statusClass = (status = "") =>
  String(status).trim().toLowerCase().replace(/\s+/g, "-");

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (time) => {
  if (!time) return "-";

  const [hours, minutes] = String(time).split(":");
  const date = new Date();
  date.setHours(Number(hours || 0), Number(minutes || 0));

  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getList = (response) => {
  const payload = response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;

  return [];
};

const currentDate = new Date();

const emptyTrend = [
  { label: "Jan", hired: 15, joined: 9, interviews: 35 },
  { label: "Feb", hired: 19, joined: 13, interviews: 47 },
  { label: "Mar", hired: 18, joined: 11, interviews: 42 },
  { label: "Apr", hired: 23, joined: 14, interviews: 42 },
  { label: "May", hired: 26, joined: 18, interviews: 52 },
  { label: "Jun", hired: 29, joined: 19, interviews: 48 },
  { label: "Jul", hired: 33, joined: 23, interviews: 55 },
];

const HRDashboard = () => {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    fromDate: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      .toISOString()
      .slice(0, 10),
    toDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10),
    company: "",
    location: "",
  });

  const [dashboard, setDashboard] = useState({});
  const [rawOpenings, setOpenings] = useState([]);
  const [rawCandidates, setCandidates] = useState([]);
  const [rawInterviews, setInterviews] = useState([]);
  const [rawOffers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError("");

      const [
        dashboardResponse,
        openingsResponse,
        candidatesResponse,
        interviewsResponse,
        offersResponse,
      ] = await Promise.all([
        getDashboard(filters),
        getJobOpenings({ limit: 500 }),
        getCandidates({ limit: 1000 }),
        getInterviews({ limit: 500 }),
        getOffers({ limit: 500 }),
      ]);

      setDashboard(
        dashboardResponse?.data?.data || dashboardResponse?.data || {},
      );
      setOpenings(getList(openingsResponse));
      setCandidates(getList(candidatesResponse));
      setInterviews(getList(interviewsResponse));
      setOffers(getList(offersResponse));
    } catch (requestError) {
      setError(requestError.message || "Unable to load HR dashboard.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, [filters.fromDate, filters.toDate, filters.company, filters.location]);

  const filterByCompanyLocation = (item, getCompany, getLocation) => {
    const companyMatch =
      !filters.company || sameValue(getCompany(item), filters.company);

    const locationMatch =
      !filters.location || sameValue(getLocation(item), filters.location);

    return companyMatch && locationMatch;
  };

  const openings = useMemo(
    () =>
      rawOpenings.filter((item) =>
        filterByCompanyLocation(item, getOpeningCompany, getOpeningLocation),
      ),
    [rawOpenings, filters.company, filters.location],
  );

  const candidates = useMemo(
    () =>
      rawCandidates.filter((item) =>
        filterByCompanyLocation(
          item,
          getCandidateCompany,
          getCandidateLocation,
        ),
      ),
    [rawCandidates, filters.company, filters.location],
  );

  const interviews = useMemo(
    () =>
      rawInterviews.filter((item) =>
        filterByCompanyLocation(
          item,
          getInterviewCompany,
          getInterviewLocation,
        ),
      ),
    [rawInterviews, filters.company, filters.location],
  );

  const offers = useMemo(
    () =>
      rawOffers.filter((item) =>
        filterByCompanyLocation(item, getOfferCompany, getOfferLocation),
      ),
    [rawOffers, filters.company, filters.location],
  );

  const companySummary = useMemo(
    () =>
      COMPANY_OPTIONS.map((companyName) => {
        const companyOpenings = rawOpenings.filter(
          (item) =>
            sameValue(getOpeningCompany(item), companyName) &&
            (!filters.location ||
              sameValue(getOpeningLocation(item), filters.location)),
        );

        const companyCandidates = rawCandidates.filter(
          (item) =>
            sameValue(getCandidateCompany(item), companyName) &&
            (!filters.location ||
              sameValue(getCandidateLocation(item), filters.location)),
        );

        const companyOffers = rawOffers.filter(
          (item) =>
            sameValue(getOfferCompany(item), companyName) &&
            (!filters.location ||
              sameValue(getOfferLocation(item), filters.location)),
        );

        return {
          company: companyName,
          openings: companyOpenings.filter(
            (item) =>
              !["closed", "cancelled"].includes(normalizeText(item?.status)),
          ).length,
          candidates: companyCandidates.length,
          offerSent: companyOffers.filter(isOfferLetterSent).length,
          joined: companyOffers.filter(
            (item) => normalizeText(item?.status) === "joined",
          ).length,
        };
      }),
    [rawOpenings, rawCandidates, rawOffers, filters.location],
  );

  const openPositions = useMemo(
    () =>
      openings.filter(
        (item) => !["Closed", "Cancelled"].includes(String(item.status || "")),
      ),
    [openings],
  );

  const todayKey = new Date().toISOString().slice(0, 10);

  const todayInterviews = useMemo(
    () =>
      interviews
        .filter(
          (item) =>
            String(item.interviewDate || "").slice(0, 10) === todayKey &&
            !["Cancelled", "Completed"].includes(item.status),
        )
        .slice(0, 4),
    [interviews, todayKey],
  );

  const recentHires = useMemo(
    () =>
      offers
        .filter((item) => item.status === "Joined")
        .sort(
          (a, b) =>
            new Date(b.expectedJoiningDate || b.updatedAt || 0) -
            new Date(a.expectedJoiningDate || a.updatedAt || 0),
        )
        .slice(0, 4),
    [offers],
  );

  const pipeline = useMemo(() => {
    const countStage = (names) =>
      candidates.filter((candidate) => names.includes(candidate.stage)).length;

    return [
      { label: "Job Posted", value: openPositions.length },
      { label: "Applications", value: candidates.length },
      {
        label: "Shortlisted",
        value: countStage(["Shortlisted"]),
      },
      {
        label: "HR Interview",
        value: countStage(["Interview Scheduled"]),
      },
      {
        label: "Technical Interview",
        value: interviews.filter((item) =>
          String(item.round || "")
            .toLowerCase()
            .includes("technical"),
        ).length,
      },
      {
        label: "Final Interview",
        value: interviews.filter((item) =>
          String(item.round || "")
            .toLowerCase()
            .includes("final"),
        ).length,
      },
      {
        label: "Offer Letter Sent",
        value: offers.filter(isOfferLetterSent).length,
      },
      {
        label: "Joined",
        value: offers.filter((item) => item.status === "Joined").length,
      },
    ];
  }, [candidates, interviews, offers, openPositions]);

  const departmentRows = useMemo(() => {
    const map = {};

    openings.forEach((opening) => {
      const department = opening.department || "Other";

      if (!map[department]) {
        map[department] = {
          department,
          openPositions: 0,
          interviews: 0,
          selected: 0,
          joined: 0,
        };
      }

      if (!["Closed", "Cancelled"].includes(opening.status)) {
        map[department].openPositions += 1;
      }
    });

    candidates.forEach((candidate) => {
      const department =
        candidate.jobOpening?.department || candidate.department || "Other";

      if (!map[department]) {
        map[department] = {
          department,
          openPositions: 0,
          interviews: 0,
          selected: 0,
          joined: 0,
        };
      }

      if (
        ["Interview Scheduled", "Interview Completed"].includes(candidate.stage)
      ) {
        map[department].interviews += 1;
      }

      if (
        [
          "Selected",
          "Offer Sent",
          "Offer Accepted",
          "Joining Confirmed",
          "Joined",
        ].includes(candidate.stage)
      ) {
        map[department].selected += 1;
      }

      if (candidate.stage === "Joined") {
        map[department].joined += 1;
      }
    });

    return Object.values(map).slice(0, 6);
  }, [openings, candidates]);

  const sourceRows = useMemo(() => {
    const map = {};

    candidates.forEach((candidate) => {
      const source = candidate.source || "Other";
      map[source] = (map[source] || 0) + 1;
    });

    const total = Math.max(1, candidates.length);

    return Object.entries(map)
      .map(([label, value]) => ({
        label,
        value,
        percent: Math.round((value / total) * 100),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [candidates]);

  const recruiterRows = useMemo(() => {
    if (Array.isArray(dashboard.recruiterPerformance)) {
      return dashboard.recruiterPerformance.slice(0, 4);
    }

    return [];
  }, [dashboard]);

  const aging = useMemo(() => {
    const buckets = [
      { label: "0 - 15 Days", min: 0, max: 15 },
      { label: "16 - 30 Days", min: 16, max: 30 },
      { label: "31 - 60 Days", min: 31, max: 60 },
      { label: "61 - 90 Days", min: 61, max: 90 },
      { label: "90+ Days", min: 91, max: Infinity },
    ];

    return buckets.map((bucket) => {
      const value = openPositions.filter((opening) => {
        const date = new Date(opening.openingDate || opening.createdAt);
        const days = Math.floor((Date.now() - date.getTime()) / 86400000);

        return days >= bucket.min && days <= bucket.max;
      }).length;

      return {
        ...bucket,
        value,
      };
    });
  }, [openPositions]);

  const monthlyTrend =
    dashboard.monthlyTrend?.length > 0 ? dashboard.monthlyTrend : emptyTrend;

  const maxTrend = Math.max(
    60,
    ...monthlyTrend.flatMap((item) => [
      Number(item.hired || 0),
      Number(item.joined || 0),
      Number(item.interviews || 0),
    ]),
  );

  const chartWidth = 620;
  const chartHeight = 220;
  const chartPadding = 28;

  const getPoints = (key) =>
    monthlyTrend
      .map((item, index) => {
        const x =
          chartPadding +
          (index * (chartWidth - chartPadding * 2)) /
            Math.max(1, monthlyTrend.length - 1);
        const y =
          chartHeight -
          chartPadding -
          (Number(item[key] || 0) / maxTrend) *
            (chartHeight - chartPadding * 2);

        return `${x},${y}`;
      })
      .join(" ");

  const kpis = [
    {
      title: "Total Open Positions",
      value: openPositions.length,
      note: "3 new this month",
      icon: "briefcase",
      className: "blue",
    },
    {
      title: "Total Candidates",
      value: candidates.length,
      note: "18% vs last month",
      icon: "candidates",
      className: "green",
    },
    {
      title: "Interviews This Month",
      value: interviews.filter((item) => {
        const date = new Date(item.interviewDate);
        return (
          date.getMonth() === currentDate.getMonth() &&
          date.getFullYear() === currentDate.getFullYear()
        );
      }).length,
      note: "12% vs last month",
      icon: "interview",
      className: "purple",
    },
    {
      title: "Hired This Month",
      value: offers.filter((item) => normalizeText(item?.status) === "joined")
        .length,
      note: "33% vs last month",
      icon: "hired",
      className: "yellow",
    },
    {
      title: "Offer Letter Sent",
      value: offers.filter(isOfferLetterSent).length,
      note: "Successfully emailed offers",
      icon: "offer",
      className: "red",
    },
    {
      title: "Joined This Month",
      value: offers.filter((item) => normalizeText(item?.status) === "joined")
        .length,
      note: "33% vs last month",
      icon: "joined",
      className: "cyan",
    },
  ];

  const quickActions = [
    {
      label: "Add Job Opening",
      icon: "briefcase",
      path: "/hr/job-openings",
    },
    {
      label: "Add Candidate",
      icon: "addCandidate",
      path: "/hr/candidates",
    },
    {
      label: "Schedule Interview",
      icon: "calendar",
      path: "/hr/interviews",
    },
    {
      label: "Send Offer",
      icon: "offer",
      path: "/hr/offers",
    },
    {
      label: "Recruitment Report",
      icon: "report",
      path: "/hr/recruitment-reports",
    },
  ];

  if (loading) {
    return (
      <div className="hrd-page">
        <div className="hrd-loading">Loading HR dashboard...</div>
      </div>
    );
  }

  return (
    <div className="hrd-page">
      <header className="hrd-topbar">
        <div>
          <h1>HR Dashboard</h1>
          <p>Welcome back, HR Admin 👋</p>
        </div>

        <div className="hrd-top-actions hrd-company-top-actions">
          <select
            className="hrd-company-filter"
            value={filters.company}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                company: event.target.value,
              }))
            }
          >
            <option value="">All Companies</option>

            {COMPANY_OPTIONS.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>

          <select
            className="hrd-location-filter"
            value={filters.location}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                location: event.target.value,
              }))
            }
          >
            <option value="">All Locations</option>

            {LOCATION_OPTIONS.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>

          <div className="hrd-date-box">
            <input
              type="date"
              value={filters.fromDate}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  fromDate: event.target.value,
                }))
              }
            />
            <span>to</span>
            <input
              type="date"
              value={filters.toDate}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  toDate: event.target.value,
                }))
              }
            />
          </div>
        </div>
      </header>

      {error && <div className="hrd-error">{error}</div>}

      <section className="hrd-company-grid">
        {companySummary.map((item) => (
          <button
            type="button"
            key={item.company}
            className={`hrd-company-card ${
              filters.company === item.company ? "active" : ""
            }`}
            onClick={() =>
              setFilters((current) => ({
                ...current,
                company: current.company === item.company ? "" : item.company,
              }))
            }
          >
            <strong>{item.company}</strong>

            <div className="hrd-company-numbers">
              <span>
                <b>{item.openings}</b>Openings
              </span>
              <span>
                <b>{item.candidates}</b>Candidates
              </span>
              <span>
                <b>{item.offerSent}</b>Offer Letter Sent
              </span>
              <span>
                <b>{item.joined}</b>Joined
              </span>
            </div>
          </button>
        ))}
      </section>

      <section className="hrd-kpi-grid">
        {kpis.map((item) => (
          <article
            className={`hrd-kpi-card hrd-kpi-${item.className}`}
            key={item.title}
          >
            <div>
              <span>{item.title}</span>
              <strong>{item.value}</strong>
              <small>↗ {item.note}</small>
            </div>

            <div className="hrd-kpi-icon">
              <Icon type={item.icon} />
            </div>
          </article>
        ))}
      </section>

      <section className="hrd-main-grid">
        <article className="hrd-card hrd-pipeline-card">
          <div className="hrd-card-head">
            <h2>Hiring Pipeline</h2>
          </div>

          <div className="hrd-funnel">
            {pipeline.map((item, index) => {
              const width = 100 - index * 9;

              return (
                <div className="hrd-funnel-row" key={item.label}>
                  <span>{item.label}</span>

                  <div className="hrd-funnel-shape-wrap">
                    <div
                      className={`hrd-funnel-shape hrd-funnel-${index + 1}`}
                      style={{ width: `${Math.max(28, width)}%` }}
                    />
                  </div>

                  <strong>{item.value}</strong>
                </div>
              );
            })}
          </div>
        </article>

        <article className="hrd-card hrd-trend-card">
          <div className="hrd-card-head">
            <h2>Monthly Hiring Trend</h2>

            <select defaultValue="This Year">
              <option>This Year</option>
              <option>Last Year</option>
            </select>
          </div>

          <div className="hrd-chart-legend">
            <span>
              <i className="green" /> Hired
            </span>
            <span>
              <i className="blue" /> Joined
            </span>
            <span>
              <i className="purple" /> Interviews
            </span>
          </div>

          <div className="hrd-trend-chart">
            <svg
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              preserveAspectRatio="none"
            >
              {[0, 1, 2, 3, 4].map((line) => {
                const y =
                  chartPadding + (line * (chartHeight - chartPadding * 2)) / 4;

                return (
                  <line
                    key={line}
                    x1={chartPadding}
                    x2={chartWidth - chartPadding}
                    y1={y}
                    y2={y}
                    className="hrd-grid-line"
                  />
                );
              })}

              <polyline
                points={getPoints("interviews")}
                className="hrd-line hrd-line-purple"
              />

              {monthlyTrend.map((item, index) => {
                const sectionWidth =
                  (chartWidth - chartPadding * 2) / monthlyTrend.length;
                const x = chartPadding + index * sectionWidth + 10;
                const hiredHeight =
                  (Number(item.hired || 0) / maxTrend) *
                  (chartHeight - chartPadding * 2);
                const joinedHeight =
                  (Number(item.joined || 0) / maxTrend) *
                  (chartHeight - chartPadding * 2);

                return (
                  <React.Fragment key={item.label || index}>
                    <rect
                      x={x}
                      y={chartHeight - chartPadding - hiredHeight}
                      width="14"
                      height={hiredHeight}
                      className="hrd-bar-green"
                    />

                    <rect
                      x={x + 18}
                      y={chartHeight - chartPadding - joinedHeight}
                      width="14"
                      height={joinedHeight}
                      className="hrd-bar-blue"
                    />
                  </React.Fragment>
                );
              })}
            </svg>

            <div className="hrd-chart-labels">
              {monthlyTrend.map((item, index) => (
                <span key={item.label || index}>
                  {item.label || item.month}
                </span>
              ))}
            </div>
          </div>
        </article>

        <article className="hrd-card hrd-side-card">
          <div className="hrd-card-head">
            <h2>Upcoming Interviews (Today)</h2>

            <button onClick={() => navigate("/hr/interviews")}>View All</button>
          </div>

          <div className="hrd-interview-list">
            {todayInterviews.length ? (
              todayInterviews.map((item) => (
                <div className="hrd-interview-row" key={item._id}>
                  <strong>{formatTime(item.interviewTime)}</strong>

                  <div className="hrd-avatar">
                    {(
                      item.candidate?.candidateName ||
                      item.candidateName ||
                      "C"
                    ).charAt(0)}
                  </div>

                  <div className="hrd-person-info">
                    <span>
                      {item.candidate?.candidateName ||
                        item.candidateName ||
                        "Candidate"}
                    </span>
                    <small>
                      {item.jobOpening?.jobTitle ||
                        item.candidate?.jobOpening?.jobTitle ||
                        "Position"}
                    </small>
                  </div>

                  <span className="hrd-interviewer">
                    {item.interviewerName ||
                      item.interviewer?.name ||
                      "HR Team"}
                  </span>

                  <span className={`hrd-status ${statusClass(item.status)}`}>
                    {item.status || "Scheduled"}
                  </span>
                </div>
              ))
            ) : (
              <div className="hrd-empty">No interviews scheduled today.</div>
            )}
          </div>
        </article>

        <article className="hrd-card hrd-department-card">
          <div className="hrd-card-head">
            <h2>Hiring by Department</h2>
          </div>

          <div className="hrd-table-wrap">
            <table className="hrd-table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th>Open Positions</th>
                  <th>Interviews</th>
                  <th>Selected</th>
                  <th>Joined</th>
                </tr>
              </thead>

              <tbody>
                {departmentRows.map((item, index) => (
                  <tr key={item.department}>
                    <td>
                      <span className={`hrd-dept-icon hrd-dept-${index + 1}`}>
                        🏢
                      </span>
                      {item.department}
                    </td>
                    <td>{item.openPositions}</td>
                    <td>{item.interviews}</td>
                    <td>{item.selected}</td>
                    <td>{item.joined}</td>
                  </tr>
                ))}

                {!departmentRows.length && (
                  <tr>
                    <td colSpan="5" className="hrd-empty">
                      No department hiring data.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="hrd-card hrd-source-card">
          <div className="hrd-card-head">
            <h2>Candidates by Source</h2>
          </div>

          <div className="hrd-source-content">
            <div
              className="hrd-donut"
              style={{
                background:
                  sourceRows.length > 0
                    ? `conic-gradient(
                        #3478e5 0 40%,
                        #32b489 40% 65%,
                        #f5bf3f 65% 80%,
                        #9d62d5 80% 92%,
                        #ef6599 92% 100%
                      )`
                    : "#e8edf5",
              }}
            >
              <div>
                <strong>{candidates.length}</strong>
                <span>Total</span>
              </div>
            </div>

            <div className="hrd-source-list">
              {sourceRows.map((item, index) => (
                <div key={item.label}>
                  <span>
                    <i className={`source-${index + 1}`} />
                    {item.label}
                  </span>

                  <strong>
                    {item.percent}% ({item.value})
                  </strong>
                </div>
              ))}
            </div>
          </div>
        </article>

        <div className="hrd-right-stack">
          <article className="hrd-card">
            <div className="hrd-card-head">
              <h2>Recent Hires</h2>

              <button onClick={() => navigate("/hr/joining")}>View All</button>
            </div>

            <div className="hrd-table-wrap">
              <table className="hrd-table hrd-small-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Position</th>
                    <th>Joining Date</th>
                    <th>Salary</th>
                  </tr>
                </thead>

                <tbody>
                  {recentHires.length ? (
                    recentHires.map((item) => (
                      <tr key={item._id}>
                        <td>
                          <span className="hrd-mini-avatar">
                            {(
                              item.candidate?.candidateName ||
                              item.candidateName ||
                              "C"
                            ).charAt(0)}
                          </span>

                          {item.candidate?.candidateName ||
                            item.candidateName ||
                            "Candidate"}
                        </td>

                        <td>
                          {item.offeredDesignation ||
                            item.jobOpening?.jobTitle ||
                            "-"}
                        </td>

                        <td>{formatDate(item.expectedJoiningDate)}</td>

                        <td>
                          {item.offeredSalary
                            ? `₹${Number(item.offeredSalary).toLocaleString(
                                "en-IN",
                              )}`
                            : "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="hrd-empty">
                        No recent hires.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>

          <article className="hrd-card">
            <div className="hrd-card-head">
              <h2>Recruiter Performance (This Month)</h2>

              <button>View All</button>
            </div>

            <div className="hrd-table-wrap">
              <table className="hrd-table hrd-small-table">
                <thead>
                  <tr>
                    <th>Recruiter</th>
                    <th>Interviews</th>
                    <th>Offers</th>
                    <th>Joined</th>
                    <th>Conversion Rate</th>
                  </tr>
                </thead>

                <tbody>
                  {recruiterRows.length ? (
                    recruiterRows.map((item, index) => (
                      <tr key={item.recruiter || index}>
                        <td>{item.recruiter || item.name}</td>
                        <td>{item.interviews || 0}</td>
                        <td>{item.offers || 0}</td>
                        <td>{item.joined || 0}</td>
                        <td>
                          <div className="hrd-conversion">
                            <span>{item.conversionRate || 0}%</span>
                            <i>
                              <b
                                style={{
                                  width: `${Math.min(
                                    100,
                                    Number(item.conversionRate || 0),
                                  )}%`,
                                }}
                              />
                            </i>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="hrd-empty">
                        Recruiter performance API data is not available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>
        </div>
      </section>

      <section className="hrd-bottom-grid">
        <article className="hrd-card">
          <div className="hrd-card-head">
            <h2>Open Positions Aging</h2>
          </div>

          <div className="hrd-aging-grid">
            {aging.map((item, index) => (
              <div
                className={`hrd-aging-card aging-${index + 1}`}
                key={item.label}
              >
                <span>{item.label}</span>
                <strong>
                  {item.value}
                  <small>{item.value === 1 ? " Position" : " Positions"}</small>
                </strong>
              </div>
            ))}
          </div>
        </article>

        <article className="hrd-card">
          <div className="hrd-card-head">
            <h2>Quick Actions</h2>
          </div>

          <div className="hrd-quick-grid">
            {quickActions.map((item) => (
              <button key={item.label} onClick={() => navigate(item.path)}>
                <Icon type={item.icon} size={30} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
};

export default HRDashboard;
